import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { BasicInfoSection, WorkConditionSection } from "./shift-code-form-dialog-sections";
import { WantedSwitchField } from "./shift-code-add-dialog-extra-fields";
import {
  canUseOffSwapTarget,
  SLOT_OPTIONS,
  type WorkType,
} from "./shift-code-add-dialog-utils";
import {
  getOffSwapUnavailableMessage,
  resolveWorkType,
  type ShiftCodeFormInitialValue,
  type ShiftCodeFormSubmitValue,
} from "./shift-code-form-dialog-model";

export type { ShiftCodeFormSubmitValue } from "./shift-code-form-dialog-model";

function getWantedPreferenceRule({
  defaultShift,
  shiftGb,
}: {
  readonly defaultShift?: string | null;
  readonly shiftGb?: string | null;
}): {
  readonly checked: boolean | null;
  readonly disabled: boolean;
  readonly disabledReason: string | null;
} {
  if (defaultShift && defaultShift !== "주") {
    return {
      checked: true,
      disabled: true,
      disabledReason:
        "기본 근무코드는 원티드 신청 기준 코드라서 반영 여부를 변경할 수 없습니다.",
    };
  }
  if (shiftGb === null) {
    return { checked: null, disabled: false, disabledReason: null };
  }
  return {
    checked: false,
    disabled: true,
    disabledReason:
      "구분이 있는 근무코드는 원티드 신청 화면에 별도 선택 코드로 노출하지 않습니다.",
  };
}

export function ShiftCodeFormDialog({
  open,
  mode,
  title,
  description,
  initialValue,
  initialSlotIndex,
  duplicateShiftIds,
  identityDisabled = false,
  shiftCodeDisabled = false,
  workStructureDisabled = false,
  readonlySlotLabel = null,
  offSwapEditable = false,
  offSwapOffShiftGb,
  isPending = false,
  onOpenChange,
  onSubmit,
}: {
  readonly open: boolean;
  readonly mode: "add" | "edit";
  readonly title: string;
  readonly description: string;
  readonly initialValue: ShiftCodeFormInitialValue;
  readonly initialSlotIndex: number;
  readonly duplicateShiftIds: readonly string[];
  readonly identityDisabled?: boolean;
  readonly shiftCodeDisabled?: boolean;
  readonly workStructureDisabled?: boolean;
  readonly readonlySlotLabel?: string | null;
  readonly offSwapEditable?: boolean;
  readonly offSwapOffShiftGb?: string | null;
  readonly isPending?: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSubmit: (value: ShiftCodeFormSubmitValue) => void;
}) {
  const shiftInputRef = useRef<HTMLInputElement>(null);
  const initialShiftId = initialValue.shift_id.slice(0, 3);
  const [shiftId, setShiftId] = useState(initialShiftId);
  const [color, setColor] = useState(initialValue.color);
  const [shiftName, setShiftName] = useState(initialValue.name.slice(0, 20));
  const [shiftDescription, setShiftDescription] = useState(
    initialValue.description ?? ""
  );
  const [shiftIdTouched, setShiftIdTouched] = useState(mode === "edit");
  const [shiftNameTouched, setShiftNameTouched] = useState(mode === "edit");
  const [startTime, setStartTime] = useState(initialValue.start_time ?? "00:00");
  const [endTime, setEndTime] = useState(initialValue.end_time ?? "23:59");
  const [workType, setWorkType] = useState<WorkType>(() =>
    resolveWorkType(initialValue.type)
  );
  const [slotIndex, setSlotIndex] = useState(initialSlotIndex);
  const [showInPreference, setShowInPreference] = useState(
    Boolean(initialValue.show_in_preference)
  );
  const [offSwapTarget, setOffSwapTarget] = useState(
    Boolean(initialValue.off_swap_target)
  );
  const [healthLeaveTarget, setHealthLeaveTarget] = useState(
    Boolean(initialValue.health_leave_target)
  );
  const [sleepOffTarget, setSleepOffTarget] = useState(
    Boolean(initialValue.sleep_off_target)
  );

  const trimmedShiftId = shiftId.trim();
  const submittedShiftId = shiftCodeDisabled ? initialShiftId : trimmedShiftId;
  const trimmedShiftName = shiftName.trim();
  const trimmedShiftDescription = shiftDescription.trim();
  const isDuplicateShiftId =
    trimmedShiftId !== initialShiftId &&
    duplicateShiftIds.includes(trimmedShiftId);
  const submittedType = workStructureDisabled ? initialValue.type : workType;
  const isWorkShift = submittedType === "근무";
  const selectedSlot = SLOT_OPTIONS.find((item) => item.value === slotIndex);
  const targetShiftGb = isWorkShift
    ? (selectedSlot?.shiftGb ?? null)
    : (initialValue.shift_gb ?? null);
  const wantedPreferenceRule = getWantedPreferenceRule({
    defaultShift: initialValue.default_shift,
    shiftGb: targetShiftGb,
  });
  const displayedShowInPreference =
    wantedPreferenceRule.checked ?? showInPreference;
  const offSwapTargetEditable =
    offSwapEditable &&
    canUseOffSwapTarget(
      {
        type: submittedType,
        default_shift: initialValue.default_shift,
        shift_gb: targetShiftGb,
      },
      offSwapOffShiftGb
    );
  const offSwapUnavailableMessage = getOffSwapUnavailableMessage({
    type: submittedType,
    defaultShift: initialValue.default_shift,
    shiftGb: targetShiftGb,
    offSwapOffShiftGb,
  });

  const errors = useMemo(
    () => ({
      shiftId:
        trimmedShiftId.length === 0
          ? "근무코드를 입력해주세요."
          : isDuplicateShiftId
            ? "동일한 근무코드가 존재합니다."
            : undefined,
      shiftName:
        trimmedShiftName.length === 0 ? "이름을 입력해주세요." : undefined,
    }),
    [isDuplicateShiftId, trimmedShiftId, trimmedShiftName]
  );

  const canSubmit =
    !isPending && errors.shiftId == null && errors.shiftName == null;

  useEffect(() => {
    if (mode === "add" && window.matchMedia("(min-width: 768px)").matches) {
      shiftInputRef.current?.focus();
    }
  }, [mode]);

  useEffect(() => {
    if (!isWorkShift) {
      setSlotIndex(0);
    }
  }, [isWorkShift]);

  useEffect(() => {
    if (wantedPreferenceRule.checked != null) {
      setShowInPreference(wantedPreferenceRule.checked);
    }
  }, [wantedPreferenceRule.checked]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;

    onSubmit({
      shift_id: submittedShiftId,
      name: trimmedShiftName,
      description:
        trimmedShiftDescription.length > 0 ? trimmedShiftDescription : null,
      color,
      start_time: isWorkShift ? startTime : null,
      end_time: isWorkShift ? endTime : null,
      type: submittedType,
      shift_gb: isWorkShift ? (selectedSlot?.shiftGb ?? null) : null,
      show_in_preference: displayedShowInPreference,
      off_swap_target: offSwapTargetEditable ? offSwapTarget : false,
      // 서버가 type==='근무' 를 400 으로 거절하므로 근무면 보내지 않는다.
      health_leave_target: isWorkShift ? false : healthLeaveTarget,
      sleep_off_target: isWorkShift ? false : sleepOffTarget,
      slotIndex: isWorkShift && slotIndex !== 4 ? slotIndex : 0,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[calc(100dvh-32px)] w-[min(720px,calc(100vw-32px))] max-w-none gap-0 overflow-hidden rounded-[10px] border-0 bg-white p-0 font-pretendard shadow-lg"
        overlayClassName="bg-black/45"
      >
        <form
          className="flex max-h-[calc(100dvh-32px)] min-h-0 flex-col overflow-hidden bg-white text-[#101828]"
          onSubmit={handleSubmit}
        >
          <DialogHeader className="shrink-0 border-b border-[#E4E7EC] bg-white px-6 pb-4 pr-12 pt-5 text-left">
            <p className="text-xs font-bold text-[#0E59C6]">근무코드 설정</p>
            <DialogTitle className="mt-1 text-[22px] font-bold leading-tight tracking-normal text-[#101828]">
              {title}
            </DialogTitle>
            <DialogDescription className="mt-1 text-sm leading-5 text-[#667085] break-keep">
              {description}
            </DialogDescription>
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-y-auto bg-[#F8FAFC] px-6 py-5">
            <div className="grid gap-4">
              <BasicInfoSection
                shiftInputRef={shiftInputRef}
                shiftId={shiftId}
                color={color}
                shiftIdError={shiftIdTouched ? errors.shiftId : undefined}
                shiftName={shiftName}
                shiftNameError={shiftNameTouched ? errors.shiftName : undefined}
                shiftDescription={shiftDescription}
                identityDisabled={identityDisabled}
                shiftCodeDisabled={shiftCodeDisabled}
                onShiftIdChange={(value) => {
                  setShiftIdTouched(true);
                  setShiftId(value);
                }}
                onColorChange={setColor}
                onShiftNameChange={(value) => {
                  setShiftNameTouched(true);
                  setShiftName(value);
                }}
                onShiftDescriptionChange={setShiftDescription}
              />
              <WorkConditionSection
                workType={workType}
                workStructureDisabled={workStructureDisabled}
                isWorkShift={isWorkShift}
                slotIndex={slotIndex}
                readonlySlotLabel={readonlySlotLabel}
                startTime={startTime}
                endTime={endTime}
                offSwapEditable={offSwapTargetEditable}
                offSwapUnavailableMessage={offSwapUnavailableMessage}
                offSwapTarget={offSwapTarget}
                healthLeaveTarget={healthLeaveTarget}
                sleepOffTarget={sleepOffTarget}
                onWorkTypeChange={setWorkType}
                onSlotIndexChange={setSlotIndex}
                onStartTimeChange={setStartTime}
                onEndTimeChange={setEndTime}
                onOffSwapTargetChange={setOffSwapTarget}
                onHealthLeaveTargetChange={setHealthLeaveTarget}
                onSleepOffTargetChange={setSleepOffTarget}
              />
              <WantedSwitchField
                checked={displayedShowInPreference}
                disabled={isPending || wantedPreferenceRule.disabled}
                disabledReason={wantedPreferenceRule.disabledReason}
                onChange={setShowInPreference}
              />
            </div>
          </div>

          <DialogFooter className="shrink-0 flex-col border-t border-[#E4E7EC] bg-white px-6 py-4 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outline"
              disabled={isPending}
              className="w-full sm:w-auto"
              onClick={() => onOpenChange(false)}
            >
              취소
            </Button>
            <Button
              type="submit"
              disabled={!canSubmit}
              className="w-full bg-sig text-white hover:bg-sig/90 sm:w-auto"
            >
              {mode === "add" ? "추가하기" : "저장"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
